// Reusable pan/zoom canvas widget for one render scene. Mirrors the interaction
// model of d3-vtt's "set background" editor and d3-kit's game map: wheel zooms at
// the cursor (the world point under the pointer stays put), drag pans, and the
// visible controls (− / % / + / Reset) zoom about the centre and refit. The
// scene is redrawn under a ctx.setTransform each frame, so it stays crisp at any
// zoom.
//
// Self-contained on purpose — the bench is a bundler-free static page, so this
// replicates the ~40-line pan/zoom pattern rather than cross-importing d3-kit.

import { drawTileBrush } from '../../index.js';

const VIEW_W = 380;      // canvas CSS size
const VIEW_H = 300;
const DPR = 2;           // fixed backing scale (crisp + deterministic for goldens)
const FIT_MARGIN = 0.86; // padding around the fitted scene
const MIN_SCALE = 0.2;
const MAX_SCALE = 12;
const BTN_STEP = 1.25;   // zoom factor per −/+ click

export class SceneViewer {
  /**
   * @param {object} opts
   * @param {import('../__fixtures__/scenes.js').Scene} opts.scene
   * @param {Map<string, HTMLImageElement>} opts.textures
   * @param {() => import('../tile-brushes.js').TileBrush} opts.getBrush
   *   Returns the brush to draw with (read each render, so switching brushes
   *   just needs a re-render — no viewer rebuild).
   */
  constructor({ scene, textures, getBrush }) {
    this.scene = scene;
    this.textures = textures;
    this.getBrush = getBrush;
    this.doorKeys = scene.doorKeys ? new Set(scene.doorKeys) : undefined;

    const canvas = document.createElement('canvas');
    canvas.width = VIEW_W * DPR;
    canvas.height = VIEW_H * DPR;
    canvas.style.width = `${VIEW_W}px`;
    canvas.style.height = `${VIEW_H}px`;
    canvas.dataset.scene = scene.name;
    canvas.title = 'scroll to zoom · drag to pan · double-click to reset';
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');

    this.element = document.createElement('div');
    this.element.className = 'viewer';
    this.element.append(canvas, this._buildControls());

    this._bindEvents();
    this.fit();
  }

  _buildControls() {
    const bar = document.createElement('div');
    bar.className = 'view-controls';
    const mk = (text, title, fn) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.textContent = text;
      b.title = title;
      b.addEventListener('click', fn);
      return b;
    };
    this._readout = document.createElement('span');
    this._readout.className = 'zoom-readout';
    bar.append(
      mk('−', 'Zoom out', () => this.zoomBy(1 / BTN_STEP)),
      this._readout,
      mk('+', 'Zoom in', () => this.zoomBy(BTN_STEP)),
      mk('Reset', 'Fit to view', () => this.fit()),
    );
    return bar;
  }

  /** Content size in world px (cells × cellPx). */
  _contentSize() {
    return { w: this.scene.gridW * this.scene.cellPx, h: this.scene.gridH * this.scene.cellPx };
  }

  /** Reset to the centred, fitted view. */
  fit() {
    const { w, h } = this._contentSize();
    this.scale = Math.min(VIEW_W / w, VIEW_H / h) * FIT_MARGIN;
    this.panX = (VIEW_W - this.scale * w) / 2;
    this.panY = (VIEW_H - this.scale * h) / 2;
    this.render();
  }

  /** Zoom by a factor about the viewport centre. */
  zoomBy(factor) {
    this._zoomAt(VIEW_W / 2, VIEW_H / 2, factor);
  }

  /** Zoom about a point (CSS px in the canvas), keeping that world point fixed. */
  _zoomAt(sx, sy, factor) {
    const next = Math.min(MAX_SCALE, Math.max(MIN_SCALE, this.scale * factor));
    const worldX = (sx - this.panX) / this.scale;
    const worldY = (sy - this.panY) / this.scale;
    this.scale = next;
    this.panX = sx - worldX * next;
    this.panY = sy - worldY * next;
    this.render();
  }

  _bindEvents() {
    const c = this.canvas;
    c.addEventListener('wheel', (e) => {
      e.preventDefault();
      this._zoomAt(e.offsetX, e.offsetY, Math.exp(-e.deltaY * 0.0015));
    }, { passive: false });

    c.addEventListener('pointerdown', (e) => {
      this._drag = { x: e.offsetX, y: e.offsetY, panX: this.panX, panY: this.panY };
      c.setPointerCapture(e.pointerId);
      c.style.cursor = 'grabbing';
    });
    c.addEventListener('pointermove', (e) => {
      if (!this._drag) return;
      this.panX = this._drag.panX + (e.offsetX - this._drag.x);
      this.panY = this._drag.panY + (e.offsetY - this._drag.y);
      this.render();
    });
    const endDrag = (e) => {
      this._drag = null;
      c.style.cursor = 'grab';
      if (e.pointerId != null && c.hasPointerCapture?.(e.pointerId)) c.releasePointerCapture(e.pointerId);
    };
    c.addEventListener('pointerup', endDrag);
    c.addEventListener('pointercancel', endDrag);
    c.addEventListener('dblclick', () => this.fit());
    c.style.cursor = 'grab';
  }

  _drawGrid() {
    const { w, h } = this._contentSize();
    const { ctx, scene } = this;
    ctx.fillStyle = '#fbfbfc';
    ctx.fillRect(0, 0, w, h);
    ctx.strokeStyle = '#e6e8eb';
    ctx.lineWidth = 1 / this.scale;
    for (let x = 0; x <= scene.gridW; x++) {
      ctx.beginPath();
      ctx.moveTo(x * scene.cellPx, 0);
      ctx.lineTo(x * scene.cellPx, h);
      ctx.stroke();
    }
    for (let y = 0; y <= scene.gridH; y++) {
      ctx.beginPath();
      ctx.moveTo(0, y * scene.cellPx);
      ctx.lineTo(w, y * scene.cellPx);
      ctx.stroke();
    }
  }

  render() {
    const { ctx } = this;
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, VIEW_W, VIEW_H);
    ctx.setTransform(DPR * this.scale, 0, 0, DPR * this.scale, DPR * this.panX, DPR * this.panY);
    this._drawGrid();
    drawTileBrush(ctx, this.scene.walls, this.getBrush(), this.textures, this.scene.cellPx, this.doorKeys);
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    if (this._readout) this._readout.textContent = `${Math.round(this.scale * 100)}%`;
  }
}
